import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import ReviewTabs from './ReviewTabs';

const SCOPES = ['global', 'product', 'feature', 'path'];
const SEVERITIES = ['info', 'warning', 'error'];
const EMPTY = { scope: 'global', scope_ref: '', category: '', severity: 'warning', title: '', rule_text: '', enabled: true };

export default function ReviewRules() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState(null);   // editing rule or new EMPTY

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get('/code-review/rules'); setRules(res.data.data || []); }
    catch (e) { setError(e.response?.data?.error || 'Failed to load rules'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function save() {
    if (!draft.title.trim() || !draft.rule_text.trim()) { alert('Title and rule text are required'); return; }
    try {
      if (draft.id) await axios.put(`/code-review/rules/${draft.id}`, draft);
      else await axios.post('/code-review/rules', draft);
      setDraft(null); load();
    } catch (e) { alert(e.response?.data?.error || 'Failed to save'); }
  }
  async function remove(r) {
    if (!window.confirm(`Delete rule "${r.title}"?`)) return;
    try { await axios.delete(`/code-review/rules/${r.id}`); load(); }
    catch (e) { alert(e.response?.data?.error || 'Failed to delete'); }
  }
  async function toggle(r) {
    try { await axios.put(`/code-review/rules/${r.id}`, { enabled: !r.enabled }); load(); }
    catch (e) { alert(e.response?.data?.error || 'Failed'); }
  }

  return (
    <div>
      <div className="page-header">
        <h2>Custom Rules</h2>
        <p>Natural-language rules given to the reviewer. Leave config blank to apply everywhere.</p>
      </div>
      <div className="page-body">
        <ReviewTabs />
        <div className="toolbar">
          <div className="spacer" />
          <button className="btn btn-primary" onClick={() => setDraft({ ...EMPTY })}>+ New rule</button>
        </div>
        {error && <div className="error-msg">{error}</div>}

        {draft && (
          <div className="card" style={{ padding: 20, marginBottom: 16 }}>
            <h3 style={{ marginTop: 0, fontSize: 15 }}>{draft.id ? 'Edit rule' : 'New rule'}</h3>
            <div className="form-group">
              <label className="form-label">Title</label>
              <input className="form-control" value={draft.title} onChange={e => setDraft({ ...draft, title: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Rule (instruction to the reviewer)</label>
              <textarea className="form-control" rows={3} value={draft.rule_text} onChange={e => setDraft({ ...draft, rule_text: e.target.value })}
                placeholder="e.g. Any change to retention logic must respect the configurable retention window." />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">Scope</label>
                <select className="form-control" value={draft.scope} onChange={e => setDraft({ ...draft, scope: e.target.value })}>
                  {SCOPES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Scope ref</label>
                <input className="form-control" value={draft.scope_ref || ''} onChange={e => setDraft({ ...draft, scope_ref: e.target.value })}
                  placeholder={draft.scope === 'path' ? 'src/**' : draft.scope === 'global' ? '(n/a)' : 'id'} disabled={draft.scope === 'global'} />
              </div>
              <div className="form-group">
                <label className="form-label">Category</label>
                <input className="form-control" value={draft.category || ''} onChange={e => setDraft({ ...draft, category: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Severity</label>
                <select className="form-control" value={draft.severity} onChange={e => setDraft({ ...draft, severity: e.target.value })}>
                  {SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-secondary" onClick={() => setDraft(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={save}>Save</button>
            </div>
          </div>
        )}

        {loading ? <div className="spinner" /> : rules.length === 0 ? (
          <div className="empty-state"><h3>No custom rules</h3><p>Standard rule packs still apply; add custom rules for project-specific checks.</p></div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {rules.map(r => (
              <div key={r.id} style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', background: 'var(--bg-surface)', padding: '14px 18px', opacity: r.enabled ? 1 : 0.55 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <b>{r.title}</b>
                  <span className="badge" style={{ background: 'var(--bg-elevated)', color: 'var(--text-muted)' }}>{r.scope}{r.scope_ref ? `: ${r.scope_ref}` : ''}</span>
                  <span className="badge" style={{ background: 'var(--bg-elevated)', color: 'var(--text-muted)' }}>{r.severity}</span>
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                    <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => toggle(r)}>{r.enabled ? 'Disable' : 'Enable'}</button>
                    <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => setDraft(r)}>Edit</button>
                    <button className="btn btn-ghost" style={{ fontSize: 12, color: 'var(--danger)' }} onClick={() => remove(r)}>Delete</button>
                  </div>
                </div>
                <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 6, marginBottom: 0 }}>{r.rule_text}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
